/**
 * Pure maths for the VR build tools (no React, no scene): hand-twist tracking,
 * the grab transform, smoothing, the two-hand solve, proportional scale and the
 * Turn-mode angle. Everything writes into caller-owned objects — nothing is
 * allocated per frame. Unit-tested in tests/vrBuildMath.test.ts.
 */
import * as THREE from 'three';

export const TAU = Math.PI * 2;
/** Wrap an angle to -π..π. */
export const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const UP = new THREE.Vector3(0, 1, 0);

// ---------------------------------------------------------------------------
// Hand / controller twist → yaw
// ---------------------------------------------------------------------------

/**
 * Turns a hand's (or controller's) orientation into a yaw for the thing it
 * holds — the "knob" mapping: roll the wrist (or the controller) about the
 * way it points and the object turns about its vertical axis, clockwise for
 * clockwise. Pointing straight down (grabbing from above) it's simply the
 * hand turning about vertical.
 *
 * Roll is measured against the horizon (world up), so sweeping the arm — which
 * yaws and pitches the hand — doesn't turn the object; only twisting does.
 * Deltas are accumulated frame to frame and blended between the two measures
 * by how vertical the hand points, so there's never a jump.
 */
export class TwistTracker {
  /** Accumulated yaw (rad) since reset. */
  yaw = 0;
  private roll = 0;
  private heading = 0;
  private hasRoll = false;
  private hasHeading = false;
  private r = new THREE.Vector3();
  private c = new THREE.Vector3();

  /** Start from this pose (fwd = pointing direction, up = any vector across it that turns with the hand). */
  reset(fwd: THREE.Vector3, up: THREE.Vector3) { this.yaw = 0; this.measure(fwd, up); }

  /** Feed this frame's pose; returns the accumulated yaw. */
  update(fwd: THREE.Vector3, up: THREE.Vector3): number {
    const prevRoll = this.roll, prevHeading = this.heading, hadRoll = this.hasRoll, hadHeading = this.hasHeading;
    const w = this.measure(fwd, up);
    const dRoll = hadRoll && this.hasRoll ? wrapAngle(this.roll - prevRoll) : 0;
    const dHead = hadHeading && this.hasHeading ? wrapAngle(this.heading - prevHeading) : 0;
    // Clockwise roll seen from behind the hand = clockwise seen from above = negative yaw.
    this.yaw += -(1 - w) * dRoll + w * dHead;
    return this.yaw;
  }

  /** Updates roll / heading; returns the blend weight of heading (1 = pointing straight up/down). */
  private measure(fwd: THREE.Vector3, up: THREE.Vector3): number {
    const fy = Math.abs(fwd.y);
    // Roll: angle about fwd from the horizon reference (world up projected ⟂ fwd) to `up`.
    this.r.copy(UP).addScaledVector(fwd, -fwd.y);
    const rl = this.r.length();
    this.hasRoll = rl > 1e-3;
    if (this.hasRoll) {
      this.r.multiplyScalar(1 / rl);
      this.c.crossVectors(this.r, up);
      this.roll = Math.atan2(this.c.dot(fwd), this.r.dot(up));
    }
    // Heading: yaw of `up` seen from above (meaningful when the hand points down / up).
    const hl = Math.hypot(up.x, up.z);
    this.hasHeading = hl > 1e-3;
    if (this.hasHeading) this.heading = Math.atan2(up.x, up.z);
    let w = THREE.MathUtils.smoothstep(fy, 0.75, 0.95);
    if (!this.hasRoll) w = 1;
    if (!this.hasHeading) w = 0;
    return w;
  }
}

/** Where a held object goes: the grab point plus the grab-time offset turned by `yaw` about vertical. */
export function grabTarget(grabPoint: THREE.Vector3, offset: THREE.Vector3, yaw: number, out: THREE.Vector3): THREE.Vector3 {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return out.set(grabPoint.x + offset.x * c + offset.z * s, grabPoint.y + offset.y, grabPoint.z - offset.x * s + offset.z * c);
}

// ---------------------------------------------------------------------------
// Smoothing — One Euro filter (Casiez et al.): steady when still, responsive when moving
// ---------------------------------------------------------------------------

const alpha = (cutoff: number, dt: number) => { const tau = 1 / (TAU * cutoff); return 1 / (1 + tau / dt); };

export class OneEuro {
  value = 0;
  private dx = 0;
  private init = false;
  constructor(public minCutoff = 1, public beta = 0.5, public dCutoff = 1) {}
  reset(v: number) { this.value = v; this.dx = 0; this.init = true; }
  filter(v: number, dt: number): number {
    if (!this.init || dt <= 0) { this.reset(v); return v; }
    const dx = (v - this.value) / dt;
    this.dx += alpha(this.dCutoff, dt) * (dx - this.dx);
    this.value += alpha(this.minCutoff + this.beta * Math.abs(this.dx), dt) * (v - this.value);
    return this.value;
  }
}

export class OneEuro3 {
  value = new THREE.Vector3();
  private dx = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private init = false;
  constructor(public minCutoff = 1.5, public beta = 4, public dCutoff = 1) {}
  reset(v: THREE.Vector3) { this.value.copy(v); this.dx.set(0, 0, 0); this.init = true; }
  filter(v: THREE.Vector3, dt: number): THREE.Vector3 {
    if (!this.init || dt <= 0) { this.reset(v); return this.value; }
    this.tmp.copy(v).sub(this.value).multiplyScalar(1 / dt);
    this.dx.lerp(this.tmp, alpha(this.dCutoff, dt));
    this.value.lerp(v, alpha(this.minCutoff + this.beta * this.dx.length(), dt));
    return this.value;
  }
}

// ---------------------------------------------------------------------------
// Two-hand move / turn / resize
// ---------------------------------------------------------------------------

export interface TwoHandStart {
  left: THREE.Vector3; right: THREE.Vector3;
  /** Object world position, yaw and uniform-scale reference at the start. */
  pos: THREE.Vector3; yaw: number;
}
export interface TwoHandResult { pos: THREE.Vector3; yaw: number; k: number }

export const TWO_K_MIN = 0.1, TWO_K_MAX = 10;
/** Hands closer than this (horizontally) give no reliable direction: keep the last turn. */
const MIN_SPAN = 0.06;

/**
 * Both hands pinching: the midpoint drags the object, the line between the
 * hands turns it (about vertical) and their distance scales it uniformly. The
 * object keeps its place relative to the hands — it's turned and scaled about
 * the midpoint, like holding a box at both ends.
 */
export function solveTwoHand(s: TwoHandStart, left: THREE.Vector3, right: THREE.Vector3, out: TwoHandResult, kMin = TWO_K_MIN, kMax = TWO_K_MAX): TwoHandResult {
  const d0 = Math.max(0.05, s.left.distanceTo(s.right));
  const k = THREE.MathUtils.clamp(left.distanceTo(right) / d0, kMin, kMax);
  const h0x = s.right.x - s.left.x, h0z = s.right.z - s.left.z;
  const hx = right.x - left.x, hz = right.z - left.z;
  let dyaw = out.yaw - s.yaw; // keep the last turn when the span is degenerate
  if (Math.hypot(h0x, h0z) > MIN_SPAN && Math.hypot(hx, hz) > MIN_SPAN) dyaw = wrapAngle(Math.atan2(hx, hz) - Math.atan2(h0x, h0z));
  // offset from the start midpoint, turned and scaled, from the new midpoint
  const m0x = (s.left.x + s.right.x) / 2, m0y = (s.left.y + s.right.y) / 2, m0z = (s.left.z + s.right.z) / 2;
  const ox = (s.pos.x - m0x) * k, oy = (s.pos.y - m0y) * k, oz = (s.pos.z - m0z) * k;
  const c = Math.cos(dyaw), sn = Math.sin(dyaw);
  out.pos.set((left.x + right.x) / 2 + ox * c + oz * sn, (left.y + right.y) / 2 + oy, (left.z + right.z) / 2 - ox * sn + oz * c);
  out.yaw = s.yaw + dyaw;
  out.k = k;
  return out;
}

export const SNAP_YAW = Math.PI / 12; // 15°
export const SNAP_K = 0.1;
export const snapYaw = (y: number) => Math.round(y / SNAP_YAW) * SNAP_YAW;
export const snapK = (k: number) => Math.max(SNAP_K, Math.round(k / SNAP_K) * SNAP_K);
export function snapPos(p: THREE.Vector3, grid: number): THREE.Vector3 {
  return p.set(Math.round(p.x / grid) * grid, Math.max(0, Math.round(p.y / grid) * grid), Math.round(p.z / grid) * grid);
}

// ---------------------------------------------------------------------------
// Size mode / resize handle: proportional scale
// ---------------------------------------------------------------------------

export const SCALE_MIN = 0.05, SCALE_MAX = 60;

/**
 * How much bigger: the grab point's distance from the object's centre now vs
 * at the start. `minR` keeps a grab that started near the centre from blowing
 * up (it's treated as starting `minR` away; the change in distance is kept).
 */
export function sizeFactor(center: THREE.Vector3, p0: THREE.Vector3, p: THREE.Vector3, minR = 0.15): number {
  const d0 = p0.distanceTo(center), d = p.distanceTo(center);
  const r0 = Math.max(d0, minR);
  return Math.max(0.01, (r0 + (d - d0)) / r0);
}

/**
 * Scale every axis by the same factor (a wall stays a wall), clamped so the
 * smallest side stays ≥ min and the largest ≤ max.
 */
export function proportionalScale(s0: THREE.Vector3, factor: number, out: THREE.Vector3, min = SCALE_MIN, max = SCALE_MAX): THREE.Vector3 {
  const lo = Math.min(Math.abs(s0.x), Math.abs(s0.y), Math.abs(s0.z)) || 1;
  const hi = Math.max(Math.abs(s0.x), Math.abs(s0.y), Math.abs(s0.z)) || 1;
  const f = THREE.MathUtils.clamp(factor, min / lo, Math.max(min / lo, max / hi));
  return out.copy(s0).multiplyScalar(f);
}

// ---------------------------------------------------------------------------
// Turn mode
// ---------------------------------------------------------------------------

/**
 * Turn mode: how far the grab point has gone round the object's vertical
 * axis (rad, + = counter-clockwise from above, like rotation.y). Works the
 * same whichever way you face. Returns null when the point is too close to the
 * axis to give a direction (keep the last angle).
 */
export function turnAngle(center: THREE.Vector3, p0: THREE.Vector3, p: THREE.Vector3, minR = 0.04): number | null {
  const x0 = p0.x - center.x, z0 = p0.z - center.z, x = p.x - center.x, z = p.z - center.z;
  if (Math.hypot(x0, z0) < minR || Math.hypot(x, z) < minR) return null;
  return wrapAngle(Math.atan2(x, z) - Math.atan2(x0, z0));
}

// ---------------------------------------------------------------------------
// Two-hand start test
// ---------------------------------------------------------------------------

/** Is a pinch "on" this box: the pinch point inside it (plus margin), or the ray hitting it within `far`? */
export function pinchOnBox(box: THREE.Box3, point: THREE.Vector3, ray: THREE.Ray, far: number, margin: number, tmpBox: THREE.Box3, tmp: THREE.Vector3): boolean {
  tmpBox.copy(box).expandByScalar(margin);
  if (tmpBox.containsPoint(point)) return true;
  const hit = ray.intersectBox(tmpBox, tmp);
  return !!hit && hit.distanceTo(ray.origin) <= far;
}

// ---------------------------------------------------------------------------
// Bounds of what you see
// ---------------------------------------------------------------------------

const _bb = new THREE.Box3();
/**
 * World box of an object's visible solid meshes — skipping wireframe helpers
 * such as the 2 m selection cube drawn inside a selected model's group, which
 * Box3.setFromObject would include (and which put a model's resize handle a
 * metre away from it).
 */
export function objectBox(o: THREE.Object3D, out: THREE.Box3): THREE.Box3 {
  out.makeEmpty();
  o.updateWorldMatrix(false, true); // same cost as Box3.setFromObject (which updates each node it visits)
  o.traverseVisible((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    const mat = m.material as THREE.Material & { wireframe?: boolean };
    if (mat && !Array.isArray(mat) && mat.wireframe) return;
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    _bb.copy(m.geometry.boundingBox!).applyMatrix4(m.matrixWorld);
    out.union(_bb);
  });
  if (out.isEmpty()) out.setFromObject(o);
  return out;
}
